/**
 * Turning a detected signature field into something the viewer can point at.
 *
 * Apryse reports a field as a page number plus a bounding box, both in PDF
 * points with a top-left origin. That is the same origin WebViewer uses for
 * page coordinates, so no axis flip is needed; `analyze-signature-ink.ts`
 * renders the same rects server-side on that assumption.
 *
 * The geometry lives in pure functions so it can be exercised without a live
 * WebViewer.
 */

/** A rect as Apryse reports it: `[x1, y1, x2, y2]`. */
export type SignatureFieldRect = readonly number[];

export interface SignatureFieldBox {
  height: number;
  pageNumber: number;
  width: number;
  x: number;
  y: number;
}

export interface DetectedSignatureField {
  appearsSigned: boolean;
  confidence: number;
  pageNumber: number;
  rect: SignatureFieldRect;
  type: string;
}

/**
 * The slice of `Core.DocumentViewer` navigation needs, narrowed so callers can
 * be tested against a plain object.
 */
export interface SignatureFieldScrollTarget {
  displayPageLocation(
    pageNumber: number,
    horizontalPosition: number,
    verticalPosition: number,
    doNotJumpIfInView?: boolean
  ): void;
  getPageCount(): number;
}

/**
 * Scrolled to slightly above the field so it lands with some context above it
 * rather than flush against the top edge of the viewport.
 */
const SCROLL_MARGIN_POINTS = 80;

/**
 * Reduces a detected field to a box the viewer can scroll to or draw over.
 *
 * Returns null when the payload cannot be trusted: a rect must have all four
 * numbers and the page must be a positive integer, because this is stored as
 * JSON and a partial rect would otherwise scroll to `NaN` and blank the
 * viewer. Both axes are normalized rather than trusting that `x1 <= x2`.
 */
export function getSignatureFieldBox(field: {
  pageNumber: number;
  rect: SignatureFieldRect;
}): SignatureFieldBox | null {
  const { pageNumber, rect } = field;
  if (!Number.isInteger(pageNumber) || pageNumber < 1) return null;
  if (rect.length !== 4) return null;

  const [x1, y1, x2, y2] = rect;
  if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) return null;
  if (![x1, y1, x2, y2].every(Number.isFinite)) return null;

  const x = Math.min(x1, x2);
  const y = Math.min(y1, y2);

  return { height: Math.abs(y2 - y1), pageNumber, width: Math.abs(x2 - x1), x, y };
}

/**
 * Scrolls the viewer to a detected signature field.
 *
 * Returns whether it navigated, so a caller can leave its row unselected when
 * the payload was unusable instead of appearing to work.
 *
 * `doNotJumpIfInView` is deliberately not passed. Passing it suppresses the
 * jump whenever the target is already on screen, which on a one-page contract
 * means every field is a no-op and clicking appears to do nothing at all.
 */
export function scrollToSignatureField(
  documentViewer: SignatureFieldScrollTarget | null,
  field: { pageNumber: number; rect: SignatureFieldRect }
): boolean {
  if (!documentViewer) return false;

  const box = getSignatureFieldBox(field);
  // WebViewer clamps a page past the end to the last page, which looks like a jump to nowhere.
  if (!box || box.pageNumber > documentViewer.getPageCount()) return false;

  documentViewer.displayPageLocation(
    box.pageNumber,
    box.x,
    Math.max(0, box.y - SCROLL_MARGIN_POINTS)
  );
  return true;
}

/**
 * The fields worth showing a user, in the order they appear in the document.
 *
 * Apryse locates signature *boxes*, and a blank form is full of them, so a
 * field only counts once its pixels have been measured and read as ink,
 * matching `getIsSignatureDetected` in `@aline/common`.
 */
export function getPresentableSignatureFields<T extends DetectedSignatureField>(
  fields: readonly T[]
): T[] {
  return fields
    .filter(getFieldCountsAsSigned)
    .filter(field => getSignatureFieldBox(field) !== null)
    .toSorted((a, b) => a.pageNumber - b.pageNumber || compareTopToBottom(a, b));
}

/** Whether a field counts as evidence of signing: its pixels were measured and read as ink. */
export function getFieldCountsAsSigned(field: DetectedSignatureField): boolean {
  return field.appearsSigned;
}

function compareTopToBottom(a: DetectedSignatureField, b: DetectedSignatureField): number {
  const boxA = getSignatureFieldBox(a);
  const boxB = getSignatureFieldBox(b);
  if (!boxA || !boxB) return 0;

  return boxA.y - boxB.y;
}
